/* Pink Dream — progressive enhancement for pd-* sections.
 * Everything works without this file: links navigate, forms post to /cart/add.
 * Motion is skipped for prefers-reduced-motion and coarse pointers where costly. */
(() => {
  if (window.__pdDream) return;
  window.__pdDream = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  /* ---------- Live region (single owner) ---------- */
  let live = document.getElementById('pd-live');
  const announce = (msg) => {
    if (!live) {
      live = document.createElement('div');
      live.id = 'pd-live';
      live.className = 'pd-sr';
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(() => { live.textContent = msg; }, 60);
  };

  /* ---------- Reveal on scroll ---------- */
  const revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  const initReveal = (root) => {
    const els = root.querySelectorAll('[data-pd-reveal]:not(.is-in)');
    els.forEach((el) => {
      if (!el.style.getPropertyValue('--i')) {
        const sibs = el.parentElement ? Array.from(el.parentElement.children).filter((c) => c.hasAttribute('data-pd-reveal')) : [];
        el.style.setProperty('--i', String(Math.max(0, sibs.indexOf(el)) % 8));
      }
      if (reduce.matches || !revealIO) el.classList.add('is-in');
      else revealIO.observe(el);
    });
  };

  /* ---------- Hero: pointer parallax + sparkles ---------- */
  const initHero = (hero) => {
    if (hero.dataset.pdReady) return;
    hero.dataset.pdReady = '1';
    let raf = 0; let tx = 0; let ty = 0;
    if (fine.matches && !reduce.matches) {
      hero.addEventListener('pointermove', (ev) => {
        const r = hero.getBoundingClientRect();
        tx = clamp(((ev.clientX - r.left) / r.width) * 2 - 1, -1, 1);
        ty = clamp(((ev.clientY - r.top) / r.height) * 2 - 1, -1, 1);
        if (!raf) raf = requestAnimationFrame(() => { hero.style.setProperty('--mx', tx.toFixed(3)); hero.style.setProperty('--my', ty.toFixed(3)); raf = 0; });
      });
      hero.addEventListener('pointerleave', () => { hero.style.setProperty('--mx', '0'); hero.style.setProperty('--my', '0'); });
    }
    initSparkles(hero.querySelector('[data-pd-sparkles]'), hero);
  };

  /* Scroll-linked custom property (--sp 0..1) for elements with [data-pd-scroll] */
  const scrollEls = new Set();
  let scrollRaf = 0;
  const updateScroll = () => {
    scrollRaf = 0;
    const vh = window.innerHeight;
    for (const el of scrollEls) {
      if (!el.isConnected) { scrollEls.delete(el); continue; }
      const r = el.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh * 2) continue;
      if (el.dataset.pdScroll === 'exit') {
        el.style.setProperty('--sp', clamp(-r.top / Math.max(1, r.height), 0, 1).toFixed(3));
      } else {
        el.style.setProperty('--sp', clamp((vh - r.top) / (vh + r.height), 0, 1).toFixed(3));
      }
    }
    updateStories();
  };
  const onScroll = () => { if (!scrollRaf) scrollRaf = requestAnimationFrame(updateScroll); };

  /* ---------- Sparkle canvas ---------- */
  const initSparkles = (canvas, host) => {
    if (!canvas || reduce.matches) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const count = window.innerWidth < 750 ? 26 : 56;
    let w = 0; let h = 0; let running = false; let frame = 0;
    const parts = [];
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const spawn = (p, fresh) => {
      p.x = Math.random() * w; p.y = fresh ? Math.random() * h : h + 10;
      p.r = Math.random() * 1.8 + 0.4; p.v = Math.random() * 0.25 + 0.06;
      p.t = Math.random() * Math.PI * 2; p.s = Math.random() * 0.03 + 0.01;
      p.star = Math.random() < 0.18;
    };
    resize();
    for (let i = 0; i < count; i += 1) { const p = {}; spawn(p, true); parts.push(p); }
    const drawStar = (x, y, s, a) => {
      ctx.save(); ctx.globalAlpha = a; ctx.translate(x, y); ctx.beginPath();
      for (let i = 0; i < 4; i += 1) { ctx.rotate(Math.PI / 2); ctx.quadraticCurveTo(0, 0, 0, -s * 4); ctx.quadraticCurveTo(0, 0, s * 0.01, 0); }
      ctx.fillStyle = '#ffffff'; ctx.shadowColor = 'rgba(244,167,185,0.9)'; ctx.shadowBlur = 8; ctx.fill(); ctx.restore();
    };
    const tick = () => {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.y -= p.v; p.t += p.s; p.x += Math.sin(p.t) * 0.15;
        if (p.y < -10) spawn(p, false);
        const a = 0.35 + Math.sin(p.t * 2) * 0.35;
        if (p.star) drawStar(p.x, p.y, p.r, Math.max(0, a));
        else { ctx.globalAlpha = Math.max(0, a); ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); }
      }
      ctx.globalAlpha = 1;
      frame = requestAnimationFrame(tick);
    };
    const start = () => { if (!running) { running = true; frame = requestAnimationFrame(tick); } };
    const stop = () => { running = false; cancelAnimationFrame(frame); };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => { es[0].isIntersecting && !document.hidden ? start() : stop(); }).observe(host);
    } else start();
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    window.addEventListener('resize', () => { resize(); }, { passive: true });
  };

  /* ---------- Tilt + magnetic (fine pointers only) ---------- */
  const initTilt = (root) => {
    if (!fine.matches || reduce.matches) return;
    root.querySelectorAll('[data-pd-tilt]:not([data-pd-tilt-ready])').forEach((el) => {
      el.setAttribute('data-pd-tilt-ready', '');
      const max = parseFloat(el.dataset.pdTilt || '6');
      el.addEventListener('pointermove', (ev) => {
        const r = el.getBoundingClientRect();
        const x = (ev.clientX - r.left) / r.width - 0.5;
        const y = (ev.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--rx', (x * max).toFixed(2));
        el.style.setProperty('--ry', (-y * max).toFixed(2));
      });
      el.addEventListener('pointerleave', () => { el.style.setProperty('--rx', '0'); el.style.setProperty('--ry', '0'); });
    });
    root.querySelectorAll('[data-pd-magnetic]:not([data-pd-mag-ready])').forEach((el) => {
      el.setAttribute('data-pd-mag-ready', '');
      el.addEventListener('pointermove', (ev) => {
        const r = el.getBoundingClientRect();
        const x = ev.clientX - (r.left + r.width / 2);
        const y = ev.clientY - (r.top + r.height / 2);
        el.style.transform = `translate3d(${(x * 0.18).toFixed(1)}px, ${(y * 0.3).toFixed(1)}px, 0)`;
      });
      el.addEventListener('pointerleave', () => { el.style.transform = ''; });
    });
  };

  /* ---------- Story: words light up with scroll ---------- */
  const stories = new Set();
  const initStory = (root) => {
    root.querySelectorAll('[data-pd-words]:not([data-pd-words-ready])').forEach((el) => {
      el.setAttribute('data-pd-words-ready', '');
      const accent = (el.dataset.pdAccent || '').toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
      const words = el.textContent.trim().split(/\s+/);
      el.setAttribute('aria-label', el.textContent.trim());
      el.textContent = '';
      words.forEach((w, i) => {
        const s = document.createElement('span');
        s.className = 'pd-w';
        s.setAttribute('aria-hidden', 'true');
        s.textContent = w;
        if (accent.includes(w.toLowerCase().replace(/[^\p{L}\p{N}']/gu, ''))) s.classList.add('is-accent');
        el.appendChild(s);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      if (reduce.matches) el.querySelectorAll('.pd-w').forEach((s) => s.classList.add('is-lit'));
      else stories.add(el);
    });
  };
  const updateStories = () => {
    const vh = window.innerHeight;
    for (const el of stories) {
      if (!el.isConnected) { stories.delete(el); continue; }
      const r = el.getBoundingClientRect();
      const p = clamp((vh * 0.85 - r.top) / (r.height + vh * 0.35), 0, 1);
      const spans = el.querySelectorAll('.pd-w');
      const lit = Math.round(p * spans.length);
      spans.forEach((s, i) => s.classList.toggle('is-lit', i < lit));
    }
  };

  /* ---------- Find your glow tabs ---------- */
  const initTabs = (root) => {
    root.querySelectorAll('[data-pd-tabs]:not([data-pd-tabs-ready])').forEach((wrap) => {
      wrap.setAttribute('data-pd-tabs-ready', '');
      const tabs = Array.from(wrap.querySelectorAll('[role="tab"]'));
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) { panel.hidden = !on; if (on) initReveal(panel); }
        });
        if (focus) tab.focus();
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t, false));
        t.addEventListener('keydown', (ev) => {
          let n = -1;
          if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') n = (i + 1) % tabs.length;
          if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') n = (i - 1 + tabs.length) % tabs.length;
          if (ev.key === 'Home') n = 0;
          if (ev.key === 'End') n = tabs.length - 1;
          if (n > -1) { ev.preventDefault(); select(tabs[n], true); }
        });
      });
    });
  };

  /* ---------- Campaign thumbnails ---------- */
  const initThumbs = (root) => {
    root.querySelectorAll('[data-pd-thumbs]:not([data-pd-thumbs-ready])').forEach((list) => {
      list.setAttribute('data-pd-thumbs-ready', '');
      const target = document.getElementById(list.dataset.pdThumbs);
      list.querySelectorAll('button[data-src]').forEach((b) => {
        b.addEventListener('click', () => {
          list.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          if (!target) return;
          target.style.opacity = '0';
          window.setTimeout(() => {
            target.removeAttribute('srcset');
            target.src = b.dataset.src;
            target.style.opacity = '1';
          }, reduce.matches ? 0 : 220);
        });
      });
    });
  };

  /* ---------- Variant select swaps the card image and price ---------- */
  document.addEventListener('change', (ev) => {
    const sel = ev.target;
    if (!(sel instanceof HTMLSelectElement) || !sel.matches('[data-pd-variant]')) return;
    const opt = sel.selectedOptions[0];
    const card = sel.closest('[data-pd-product]');
    const img = card && card.querySelector('.pd-card__img--1');
    if (opt && opt.dataset.img && img) { img.removeAttribute('srcset'); img.src = opt.dataset.img; }
    const price = card && card.querySelector('[data-pd-price]');
    if (opt && opt.dataset.price && price) price.textContent = opt.dataset.price;
  });

  /* ---------- Add to bag via Shopify standard actions ---------- */
  document.addEventListener('submit', async (ev) => {
    const form = ev.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-pd-add]')) return;
    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || typeof actions.updateCart !== 'function') return; // native POST fallback
    ev.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    const label = btn && btn.querySelector('.pd-qa__label');
    const idField = form.elements.namedItem('id');
    const qtyField = form.elements.namedItem('quantity');
    const id = idField && 'value' in idField ? idField.value : '';
    const qty = Math.max(1, parseInt(qtyField && 'value' in qtyField ? qtyField.value : '1', 10) || 1);
    if (!id || !btn) return;
    const original = label ? label.textContent : '';
    btn.setAttribute('aria-busy', 'true');
    btn.classList.remove('is-added', 'is-error');
    try {
      const result = await actions.updateCart(
        { lines: [{ merchandiseId: String(id), quantity: qty }] },
        { event: { context: 'product' } }
      );
      if (result && result.userErrors && result.userErrors.length) throw new Error(result.userErrors[0].message);
      btn.classList.add('is-added');
      if (label) label.textContent = form.dataset.addedLabel || 'Added';
      announce(form.dataset.addedMessage || 'Added to your bag');
    } catch (err) {
      btn.classList.add('is-error');
      if (label) label.textContent = form.dataset.errorLabel || 'Try again';
      announce((err && err.message) || form.dataset.errorLabel || 'Could not add to bag');
    } finally {
      btn.removeAttribute('aria-busy');
      window.setTimeout(() => {
        btn.classList.remove('is-added', 'is-error');
        if (label) label.textContent = original;
      }, 2200);
    }
  });

  /* ---------- Boot + theme editor support ---------- */
  const boot = (root = document) => {
    initReveal(root);
    root.querySelectorAll('[data-pd-hero]').forEach(initHero);
    root.querySelectorAll('[data-pd-scroll]').forEach((el) => { if (!reduce.matches) scrollEls.add(el); });
    initTilt(root);
    initStory(root);
    initTabs(root);
    initThumbs(root);
    updateScroll();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', (ev) => boot(ev.target));
})();
